import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select.js";
import {
  listInformes,
  TIPO_LABEL,
  ESTADO_LABEL,
  type EstadoInforme,
  type Informe,
  type TipoInforme,
} from "@/features/reports/index.js";
import { getAllReports, mirrorServerReports } from "@/features/offline-sync/index.js";
import { getLastSync, setLastSync } from "@/shared/lib/last-sync.js";
import { StatusBadge } from "@/shared/ui/atoms/StatusBadge.js";
import { TipoBadge } from "@/shared/ui/atoms/TipoBadge.js";
import { Avatar } from "@/shared/ui/atoms/Avatar.js";
import { Input } from "@/shared/ui/input.js";
import { Button, buttonVariants } from "@/shared/ui/button.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { cn } from "@/shared/lib/utils.js";
import { usePagination } from "../lib/usePagination.js";

function formatFecha(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-PE");
}

const labelClass = "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

const thClass = "px-4 py-3 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em]";
const accionClass = "h-8 gap-1 font-mono text-[0.625rem] uppercase tracking-[0.1em]";

const TIPOS: TipoInforme[] = ["IE", "IS", "AC"];
const ESTADOS: EstadoInforme[] = ["borrador", "en_cola", "sincronizado", "enviado"];

interface InformesTabProps {
  /** Se llama tras una mutación (cambio de estado / PDF) para refrescar KPIs del padre. */
  onChange?: () => void;
}

export function InformesTab({ onChange: _onChange }: InformesTabProps) {
  const [informes, setInformes] = useState<Informe[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [busqueda, setBusqueda] = useState("");
  const [tipo, setTipo] = useState<TipoInforme | "">("");
  const [estado, setEstado] = useState<EstadoInforme | "">("");

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Token to discard stale responses (e.g. an old filter's slow reply, or a
  // background poll that resolves after a newer foreground load).
  const reqIdRef = useRef(0);

  // Carga la lista. `silent` = background poll: no spinner, no error banner.
  const cargar = useCallback(
    async (silent = false) => {
      const reqId = ++reqIdRef.current;
      const isCurrent = () => mountedRef.current && reqId === reqIdRef.current;

      if (!silent) {
        setCargando(true);
        setError(null);
      }

      // S1: IDB-first — load cached reports immediately for offline resilience.
      // Admin scope = ALL reports; no role filter on IDB reads.
      const localRecords = await getAllReports();
      const allLocal: Informe[] = localRecords.map((r) => ({
        id: r.serverId ?? r.localId,
        tipo: r.tipo,
        numero: r.numero ?? "(pendiente)",
        cliente: r.cliente ?? "",
        clienteId: r.clienteId ?? null,
        estado: r.estado,
        autorId: "",
        autorNombre: r.autorNombre ?? null,
        parentId: r.parentLocalId ?? null,
        payload: r.payload as Record<string, unknown>,
        pdfUrl: r.pdfUrl ?? null,
        createdAt: r.updatedAt,
        updatedAt: r.updatedAt,
      }));

      // Seed display from IDB immediately — keeps reports visible offline.
      // Skip on silent polls to avoid flicker (network path re-renders anyway).
      if (isCurrent() && !silent && allLocal.length > 0) {
        setInformes(allLocal);
      }

      // Incremental sync: pass ?since when we have a last-sync timestamp AND no
      // active filters (filtered requests are always full fetches to avoid missing
      // older records that match the filter but predate lastSync).
      // ponytail: when tipo/estado active, skip since — partial filtered response
      // would exclude older matching records and shrink the admin list.
      const hasActiveFilters = !!(tipo || estado);
      const lastSync = hasActiveFilters ? null : getLastSync();

      // Then try network — mirror into IDB, then render.
      try {
        const serverData = await listInformes({
          ...(tipo ? { tipo } : {}),
          ...(estado ? { estado } : {}),
          ...(lastSync ? { since: lastSync } : {}),
        });
        if (!isCurrent()) return;

        // Mirror partial/full response into IDB (idempotent upsert — guards en_cola/borrador).
        await mirrorServerReports(serverData);

        // Advance lastSync to the max updatedAt from the server response (server-authoritative).
        // Empty response → leave lastSync unchanged.
        if (serverData.length > 0) {
          const maxUpdatedAt = serverData.reduce(
            (max, r) => (r.updatedAt > max ? r.updatedAt : max),
            "",
          );
          setLastSync(maxUpdatedAt);
        }

        if (!isCurrent()) return;
        if (lastSync) {
          // Incremental path: re-read the FULL IDB after mirror so the list never
          // shrinks (server returned only changed records; IDB has all of them).
          const refreshed = await getAllReports();
          if (!isCurrent()) return;
          const mergedInformes: Informe[] = refreshed.map((r) => ({
            id: r.serverId ?? r.localId,
            tipo: r.tipo,
            numero: r.numero ?? "(pendiente)",
            cliente: r.cliente ?? "",
            clienteId: r.clienteId ?? null,
            estado: r.estado,
            autorId: "",
            autorNombre: r.autorNombre ?? null,
            parentId: r.parentLocalId ?? null,
            payload: r.payload as Record<string, unknown>,
            pdfUrl: r.pdfUrl ?? null,
            createdAt: r.updatedAt,
            updatedAt: r.updatedAt,
          }));
          setInformes(mergedInformes);
        } else {
          // Full-fetch path (no lastSync): render from server response directly.
          setInformes(serverData);
        }
        setError(null);
      } catch (err) {
        if (!isCurrent()) return;
        // Background polls never surface errors — keep the current view.
        if (silent || allLocal.length > 0) {
          // Offline but IDB has data — keep the IDB view, no error banner.
          // Do NOT advance lastSync on network failure.
          setError(null);
        } else {
          setError(friendlyMessage(err));
        }
      } finally {
        if (isCurrent() && !silent) setCargando(false);
      }
    },
    [tipo, estado],
  );

  // Initial load + reload on filter change.
  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Background auto-refresh so reports created elsewhere (e.g. a técnico's IE
  // that just synced) appear without a manual reload. Silent = no spinner.
  useEffect(() => {
    const id = setInterval(() => void cargar(true), 15_000);
    return () => clearInterval(id);
  }, [cargar]);

  const visibles = useMemo(() => {
    // Apply all three filters client-side so they work against both IDB and
    // server data. When online, tipo/estado are also filtered on the server
    // (redundant but correct). When offline, client-side filtering is the
    // only available path.
    let filtered = informes;
    if (tipo) filtered = filtered.filter((i) => i.tipo === tipo);
    if (estado) filtered = filtered.filter((i) => i.estado === estado);
    const q = busqueda.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter(
      (i) => i.numero.toLowerCase().includes(q) || i.cliente.toLowerCase().includes(q),
    );
  }, [informes, busqueda, tipo, estado]);

  const { pageItems, page, totalPages, rangeStart, rangeEnd, prev, next } =
    usePagination(visibles);

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="rounded-3xl border border-border bg-card">
        {/* Barra: búsqueda + filtros */}
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center md:p-6">
          <div className="relative w-full sm:max-w-sm">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="inf-busqueda"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Número o cliente…"
              className="rounded-full bg-secondary/40 pl-10"
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <Select value={tipo} onValueChange={(v) => setTipo(v as TipoInforme | "")}>
              <SelectTrigger className="w-full sm:w-40 rounded-full">
                <SelectValue placeholder="Tipo: todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Tipo: todos</SelectItem>
                {TIPOS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t} · {TIPO_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={estado} onValueChange={(v) => setEstado(v as EstadoInforme | "")}>
              <SelectTrigger className="w-full sm:w-40 rounded-full">
                <SelectValue placeholder="Estado: todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Estado: todos</SelectItem>
                {ESTADOS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {ESTADO_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t border-border">
          {cargando ? (
            <p className="p-6 text-sm text-muted-foreground">Cargando informes…</p>
          ) : visibles.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No hay informes que coincidan.</p>
          ) : (
            <>
              {/* Móvil: lista de tarjetas */}
              <ul className="divide-y divide-border md:hidden">
                {pageItems.map((inf) => (
                  <li key={inf.id} className="flex items-start gap-3 p-4">
                    <TipoBadge tipo={inf.tipo} />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-foreground">{inf.numero}</span>
                        <StatusBadge estado={inf.estado} />
                      </div>
                      <p className="truncate text-sm text-foreground">{inf.cliente}</p>
                      <p className="text-xs text-muted-foreground">
                        {(inf.autorNombre ?? "—") + " · " + formatFecha(inf.updatedAt)}
                      </p>
                      {inf.estado !== "borrador" && (
                        <div className="pt-2">
                          <Link
                            to={`/informe/${inf.id}/detalle`}
                            className={cn(buttonVariants({ variant: "outline", size: "sm" }), accionClass)}
                          >
                            <FileText className="size-3" aria-hidden />
                            Ver detalle
                          </Link>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Desktop: tabla */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[48rem] text-sm">
                  <thead className="border-b border-border text-left text-muted-foreground">
                    <tr>
                      <th className={thClass}>Número</th>
                      <th className={thClass}>Cliente</th>
                      <th className={thClass}>Técnico</th>
                      <th className={thClass}>Estado</th>
                      <th className={thClass}>Actualizado</th>
                      <th className={cn(thClass, "text-center")}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((inf) => (
                      <tr key={inf.id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <TipoBadge tipo={inf.tipo} />
                            <span className="font-mono text-xs text-foreground">{inf.numero}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar name={inf.cliente} />
                            <span className="text-foreground">{inf.cliente}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {inf.autorNombre ?? "—"}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge estado={inf.estado} />
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                          {formatFecha(inf.updatedAt)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap items-center justify-center gap-1.5">
                            {/* ponytail: admin is read-only — all actions live in the detail page. */}
                            {inf.estado !== "borrador" && (
                              <Link
                                to={`/informe/${inf.id}/detalle`}
                                className={cn(
                                  buttonVariants({ variant: "outline", size: "sm" }),
                                  accionClass,
                                )}
                              >
                                <FileText className="size-3" aria-hidden />
                                Ver detalle
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer: conteo + paginación */}
        <div className="flex items-center justify-between border-t border-border p-4 md:p-6">
          <span className={labelClass}>
            {cargando
              ? ""
              : `Mostrando ${rangeStart}–${rangeEnd} de ${visibles.length} informe${visibles.length === 1 ? "" : "s"}`}
          </span>
          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <span className={labelClass}>{`${page} / ${totalPages}`}</span>
            )}
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={page <= 1}
              onClick={prev}
              aria-label="Página anterior"
              className="size-8 rounded-full"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={page >= totalPages}
              onClick={next}
              aria-label="Página siguiente"
              className="size-8 rounded-full"
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
